/**
 * Detección de EventSource ZOMBI: conexión que el cliente cree viva pero por la
 * que no llega absolutamente nada.
 *
 * El watchdog existente (`SSE_REVIVE_AFTER_MS`) solo se arma cuando el cliente
 * OBSERVA una caída (`disconnected`/`reconnecting`). Si el TCP muere sin FIN
 * —cambio de red, suspender/despertar, NAT que caduca— el `EventSource` nunca
 * emite `error`: el cliente sigue diciendo "conectado" para siempre, el
 * watchdog no se arma nunca y el revive por `visibilitychange` se auto-anula
 * porque exige haber visto un error. La pestaña queda sorda hasta un F5, que es
 * exactamente el síntoma reportado ("no me llegan los mensajes hasta recargar").
 *
 * El servidor escribe un ping a TODOS los streams cada 30 s (`config/transmit.ts`),
 * así que el silencio absoluto es una señal fiable de muerte y no hace falta
 * ningún POST ni cooperación del servidor para detectarlo.
 */

/**
 * Dos pings del servidor (30 s) más margen. Por debajo de 60 s, un solo ping
 * perdido bastaría para provocar una reconexión innecesaria.
 */
export const SSE_SILENCE_TIMEOUT_MS = 90_000;

export type SseSilenceInput = {
  /** Lo que el cliente CREE sobre la conexión. */
  connected: boolean;
  /** Instante del último byte recibido por el stream, o null si aún no llegó nada. */
  lastActivityAt: number | null;
  now: number;
  timeoutMs: number;
};

export function shouldReviveForSilence(input: SseSilenceInput): boolean {
  // Si el cliente ya se sabe caído, de eso se encarga el watchdog existente:
  // duplicar la reconstrucción aquí solo añadiría carreras.
  if (!input.connected) {
    return false;
  }

  // Sin actividad registrada no hay nada que comparar. Reconstruir aquí
  // dispararía un bucle de recreación durante el arranque.
  if (input.lastActivityAt === null) {
    return false;
  }

  return input.now - input.lastActivityAt > input.timeoutMs;
}
